import React, { useEffect, useReducer, useState } from "react"

import Table from "../../components/ui/Table"
import lockedReducer from "../../lib/reducer"
import { loader } from "../../lib/loader"

import {
    Tooltip
} from "@mui/material"

import {
    Delete as DeleteIcon,
    AddBoxOutlined as AddBoxOutlinedIcon,
    AdminPanelSettings as AdminPanelSettingsIcon,
} from "@mui/icons-material"

import { getStoredUser } from "../../services/auth"
import { formatPhoneNumber, getUsers, getEnterprise, createUser } from "../../services/scout"
import { getMaxAdmins, isAccountBlocked } from "../../services/utils"
import { emitSnackbar } from "../../lib/snackbar-emitter"
import manageErrors from "../../lib/error-management"

import CreateAdmin from "./modals/createadmin"
import DeleteAdmin from "./modals/deleteadmin"

const possibleActions = ({ row, setState, blocked }) => Object.entries({
    delete: { icon: <DeleteIcon />, title: "Excluir", show: !blocked && row.id !== getStoredUser()?.id, onClick: () => setState({ delete: row }) }
})

const initialState = {
    rows: [],
    loading: false,
    total_licenses: 0,
    used_licenses: 0,
    create: null,
    delete: null
}

const adminManagementConfig = {}

const Admins = () => {

    const [enterprise, setEnterprise] = useState(null)
    const [state, setState] = useReducer(lockedReducer, initialState)

    const availableLicenses = state.total_licenses - state.used_licenses
    const isBlocked = isAccountBlocked(enterprise)

    const createAdmin = async (data) => {
        try {
            setState({ loading: true })
            const response = await createUser(data)
            if (response.status === 201) {
                emitSnackbar({ message: "Novo gestor com sucesso!", severity: "success" })
                await initialize()
                return
            }

            emitSnackbar({ message: manageErrors(response.data), severity: "warning" })
        } catch (error) {
            emitSnackbar({
                message: manageErrors(error?.data || { error: error?.message || "unknown error" }),
                severity: "error"
            })
        } finally {
            setState({ loading: false })
        }
    }

    const initialize = async () => {

        setState({ loading: true })
        loader.on()

        const [enterpriseData, users] = await Promise.all([getEnterprise(), getUsers()])
        
        const filteredUsers = users.filter((user) => !user.groups?.some(group => group.name === "administrador"))

        setEnterprise(enterpriseData)

        setState({
            ...initialState,
            total_licenses: getMaxAdmins(enterpriseData),
            used_licenses: filteredUsers?.length || 0,
            rows: filteredUsers.map(row => ({
                id: row.id,
                name: `${row.first_name} ${row.last_name}`,
                email: row.email,
                telephone: row.profile.telephone
            })) || [],
        })

        loader.off()
    }

    useEffect(() => {
        initialize()
    }, [])

    return (
        <>
            {!isBlocked && state.create && <CreateAdmin users={state.users} handleClose={() => setState({ create: null })} submit={(createAdmin)} />}
            {!isBlocked && state.delete && <DeleteAdmin user={state.delete} handleClose={() => setState({ delete: null })} handleSuccess={initialize} />}

            <section className="scout-settings-section">
                <div className="scout-settings-section__label">
                    <AdminPanelSettingsIcon />
                    <span>{adminManagementConfig?.sectionLabel || "Administradores"}: {state.used_licenses} de {state.total_licenses} licenças utilizadas</span>
                </div>

                {!state.loading && (
                    <>
                        <Table
                            columns={[
                                { id: 'name', label: 'Nome', render: (row) => row.name },
                                { id: 'email', label: 'E-mail', render: (row) => row.email },
                                { id: 'telephone', label: 'Telefone', render: (row) => formatPhoneNumber(row.telephone) },
                                {
                                    id: 'actions',
                                    label: '',
                                    align: 'right',
                                    render: (row) => (
                                        <div className="scout-settings-table__actions">
                                            {possibleActions({ row, setState, blocked: isBlocked }).map(([action, data]) => (
                                                <React.Fragment key={`${row.id}-${action}`}>
                                                    {!(!data.show && data.hide) &&
                                                        <Tooltip title={data.title}>
                                                            <span>
                                                                <button
                                                                    onClick={data.onClick}
                                                                    disabled={!data.show}
                                                                    type="button"
                                                                    className={`scout-settings-icon-action ${data.show ? "" : "disabled"}`}>
                                                                    {data.icon}
                                                                </button>
                                                            </span>
                                                        </Tooltip>
                                                    }
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    ),
                                },
                            ]}
                            data={state.rows}
                            rowKey={(row) => row.id}
                            emptyMessage="Nenhum gestor encontrado."
                            className="scout-settings-card--table"
                            tableClassName="scout-settings-table--phones"
                        />

                        <button
                            onClick={() => !isBlocked && setState({ create: true })}
                            type="button"
                            className="scout-settings-add-row"
                            disabled={isBlocked}
                        >
                            <AddBoxOutlinedIcon />
                            <span>{adminManagementConfig?.addLabel || "Adicionar"} <span style={{ fontWeight: "lighter" }}>({availableLicenses} disponíveis)</span></span>
                        </button>
                    </>
                )}

                {state.loading ? <p className="scout-inline-feedback">Atualizando gestores...</p> : null}
            </section>
        </>
    )
}

export default Admins
